import { clsx } from "clsx";
import {
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import { IOccupancyRateChart } from "../../../../interfaces";
import { useSelector } from "react-redux";
import { isMobileModeSelector } from "../../../../../redux/selectors/appStatusSelector";
import { useEffect, useRef, useState } from "react";
import classes from "./OccupancyRateBar.module.scss";
import { MONTHS_MAP } from "../../../../../shared/constants";
import { Typography } from "../../../../../shared/ui/typography";

const occupancyKeys = ["guest", "portal", "ical", "direct"];

// Tooltip box CSS class colors (already in your CSS)
const colors: Record<string, string> = {
  guest: classes.green,
  portal: classes.purple,
  ical: classes.orange,
  direct: classes.blue,
};

const compareColors: Record<string, string> = {
  guest: classes.greenCompare,
  portal: classes.purpleCompare,
  ical: classes.orangeCompare,
  direct: classes.blueCompare,
};

// HEX colors for actual bars (main year)
const colorsHex: Record<string, string> = {
  guest: "#00B48D",  // green
  portal: "#E76D83", // purple
  ical: "#5D6293",   // orange
  direct: "#E0E1EB", // blue
};

// HEX colors for compare year bars
const compareColorsHex: Record<string, string> = {
  guest: "#7FD9C8",  // green
  portal: "#F4A8B6", // purple
  ical: "#A8ABCA",   // orange
  direct: "#F2F3F8", // blue
};

const CustomTooltip = (props: any) => {
  const {
    payload,
    viewBox,
    isMobileView,
    leftTooltipHeight,
    arrowWidth,
    cartesianGridWidth,
    leftTooltipRef,
    compareMode,
    marketEnabled,
    marketLastYearEnabled,
    fromYear,
    toYear,
  } = props;

  if (!payload || payload.length === 0) return null;

  const activeData = payload[0]?.payload; // main year bar values
  const compareData =
    compareMode && fromYear !== toYear ? payload[1]?.payload : null;
    const toYearTotal = occupancyKeys.reduce((sum, key) => {
      const dataKey = `${toYear}_occupancy_rate_${key}`;
      return sum + (activeData[dataKey] || 0);
    }, 0);

    const fromYearTotal = occupancyKeys.reduce((sum, key) => {
      const dataKey = `${fromYear}_occupancy_rate_${key}`;
      return sum + (activeData[dataKey] || 0);
    }, 0);

  return (
    <div
      className={clsx(
        classes.tooltip,
        marketLastYearEnabled ? classes.marketLastYearWidth : null
      )}
      ref={leftTooltipRef}
      style={{
        bottom: (viewBox?.height * 0.3 || 0) - leftTooltipHeight,
        left: arrowWidth - 70,
        // left: 'auto',
        right: 'auto',
      }}
      >
      <div className={classes.tooltipContent}>
        {/* Header */}
        <div className={classes.tooltipHeader}>
          <Typography variant="text-sm" className={classes["gray-400"]}>
            {MONTHS_MAP[activeData.name]}, {toYear}
          </Typography>
        </div>

        {/* OCCUPANCY BLOCKS */}
        <div className={classes.tooltipGroup}>
          {/* Main Year */}
          
          {occupancyKeys.map((key) => {
            const dataKey = `${toYear}_occupancy_rate_${key}`;
            const label = key.charAt(0).toUpperCase() + key.slice(1);
            if (activeData[dataKey] === undefined) return null;
            return (
              <div className={classes.tooltipContentItem} key={dataKey}>
                <div
                  className={classes.box}
                  style={{ backgroundColor: colorsHex[key] }}
                ></div>
                <Typography
                  variant="text-sm"
                  className={classes["gray-400"]}
                  weight="medium"
                >                  
                { label === "Portal" ? `Calendar Blocks: ${activeData[dataKey]}%` : `${label}: ${activeData[dataKey]}%` }
                </Typography>
              </div>
            );
          })}   

          <div className={classes.tooltipHeader} style={{ marginTop: 1 }}>
            <Typography variant="text-sm" className={classes["gray-400"]}>
              <h3>Total: {toYearTotal}%</h3>
            </Typography>
          </div>      

          {/* Compare Year */}
          {compareData && (
            <>
              {/* Compare Year Header on Top */}
              <div className={classes.tooltipHeader} style={{ marginTop: 1 }}>
                <Typography variant="text-sm" className={classes["gray-400"]}>
                  {MONTHS_MAP[activeData.name]}, {fromYear}
                </Typography>
              </div>

              {/* Compare Year Data (Top and Bottom) */}
              {occupancyKeys.map((key) => {
                const dataKey = `${fromYear}_occupancy_rate_${key}`;
                const label = key.charAt(0).toUpperCase() + key.slice(1);

                if (compareData[dataKey] === undefined) return null;

                return (
                  <div className={classes.tooltipContentItem} key={dataKey}>
                    <div
                      className={classes.box}
                      style={{ backgroundColor: compareColorsHex[key] }}
                    ></div>
                    <Typography
                      variant="text-sm"
                      className={classes["gray-400"]}
                      weight="medium"
                    >                     
                    { label === "Portal" ? `Calendar Blocks: ${activeData[dataKey]}%` : `${label}: ${activeData[dataKey]}%` }
                    </Typography>
                  </div>
                );                
              })}
               {/* Total */}
              <div className={classes.tooltipHeader} style={{ marginTop: 1 }}>
                <Typography variant="text-sm" className={classes["gray-400"]}>
                  <h3>Total: {fromYearTotal}%</h3>
                </Typography>
              </div>
            </>
          )}          
        </div>
        {/* Market */}
        {marketEnabled && activeData && (
          <div className={classes.tooltipContentItem}>
            <div className={`${classes.box} ${classes.marketColor}`}></div>
            <Typography variant="text-md" className={classes["gray-900"]} weight="medium">
              {`Market: ${activeData.market}%`}
            </Typography>
          </div>
        )}

        {marketLastYearEnabled && activeData && (
          <div className={classes.tooltipContentItem}>
            <div className={`${classes.box} ${classes.marketLastYearColor}`}></div>
            <Typography variant="text-md" className={classes["gray-900"]} weight="medium">
              {`Market Last Year: ${activeData.marketLastYear}%`}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

const CustomYAxisTick = (props: any) => {
  const { x, y, payload, isMobileView } = props;
  return (
    <g transform={`translate(${isMobileView ? 0 : 20},${y})`}>
      <text x={0} y={0} textAnchor="start" fontSize={10} fill="#A4A7AE">
        {payload.value === 0 ? payload.value : `${payload.value}%`}
      </text>
    </g>
  );
};

export const OccupancyRateBar = ({
  data,
  fromYear,
  toYear,
  page,
  compareMode,
  marketEnabled,
  marketLastYearEnabled,
  chartType,
}: IOccupancyRateChart) => {
  const isMobileView = useSelector(isMobileModeSelector);
  const barRadius = isMobileView ? 2 : 4;
  const barWidth = isMobileView ? 6 : page === "performance" ? 16 : 8;
  const barGapWidth = isMobileView ? 2 : page === "performance" ? 8 : 4;
  const arrowWidth = isMobileView ? 6 : 10;

  const responsiveContainerRef = useRef<any>(null);
  const cartesianGridRef = useRef<any>(null);
  const leftTooltipRef = useRef<any>(null);

  const [activeTooltipIndex, setActiveTooltipIndex] = useState<number | undefined>();
  const [cartesianGridWidth, setCartesianGridWidth] = useState(0);
  const [cartesianGridHeight, setCartesianGridHeight] = useState(0);
  const [cartesianGridLeftOffset, setCartesianGridLeftOffset] = useState(0);
  const [leftTooltipWidth, setLeftTooltipWidth] = useState(0);
  const [leftTooltipHeight, setLeftTooltipHeight] = useState(0);

  useEffect(() => {
    const responsiveContainer = responsiveContainerRef?.current?.current;
    const cartesianGrid =
      cartesianGridRef?.current?._reactInternals?.child?.stateNode;
    const leftTooltip = leftTooltipRef?.current;

    cartesianGrid &&
      responsiveContainer &&
      setCartesianGridLeftOffset(
        cartesianGrid.getBoundingClientRect().left -
          responsiveContainer.getBoundingClientRect().left
      );

    cartesianGrid &&
      setCartesianGridWidth(cartesianGrid.getBoundingClientRect().width);
    cartesianGrid &&
      setCartesianGridHeight(cartesianGrid.getBoundingClientRect().height);
    leftTooltip &&
      setLeftTooltipWidth(leftTooltip.getBoundingClientRect().width);
    leftTooltip &&
      setLeftTooltipHeight(leftTooltip.getBoundingClientRect().height);
  }, [activeTooltipIndex]);

  if (!data) return null;

  interface OccupancyRow {
    [key: string]: number | string | undefined;
  }

  /**
   * Get the top stack key for each row/bar
   * @param occupancyKeys - ['guest','portal','ical','direct']
   * @param toYear - year string
   * @param data - your data array
   */
  const getTopStackKeysPerBar = (
    occupancyKeys: string[],
    toYear: string,
    data: OccupancyRow[]
  ): Record<number, string> => {
    const topKeys: Record<number, string> = {};

    data.forEach((row, index) => {
      // Map full keys
      const fullKeys = occupancyKeys.map((k) => `${toYear}_occupancy_rate_${k}`);

      // Filter keys with non-zero value
      const nonZeroStacks = fullKeys.filter((fk) => Number(row[fk] ?? 0) > 0);

      // Last non-zero stack is the top
      if (nonZeroStacks.length > 0) {
        topKeys[index] = nonZeroStacks[nonZeroStacks.length - 1];
      }
    });

    return topKeys; // {0: 'ical', 1: 'direct', ...}
  };

  // calling bar chart top key indentification helper function
  const topStackKeysForToYear = getTopStackKeysPerBar(occupancyKeys, toYear, data as OccupancyRow[]);
  const topStackKeysForFromYear = getTopStackKeysPerBar(occupancyKeys, fromYear, data as OccupancyRow[]);

  return (
    <ResponsiveContainer
      height={isMobileView ? 200 : "80%"}
      width="100%"
      ref={responsiveContainerRef}
    >
      <ComposedChart
        data={data}
        barGap={barGapWidth}
        margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
        onMouseMove={({ activeTooltipIndex }) => setActiveTooltipIndex(activeTooltipIndex)}
        onMouseLeave={() => setActiveTooltipIndex(undefined)}
      >
        <CartesianGrid
          stroke="#B4B7CF"
          strokeDasharray="2.33 4.66"
          strokeWidth={0.5}
          vertical={false}
          ref={cartesianGridRef}
        />
        <XAxis
          axisLine={false}
          dataKey="name"
          tickLine={false}
          interval={isMobileView ? 0 : undefined}
          tick={{ fill: "#9798A5", fontSize: isMobileView ? 10 : 14 }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={<CustomYAxisTick isMobileView={isMobileView} />}
          width={isMobileView ? 24 : 60}
        />

        <Tooltip
          position={{
            x: activeTooltipIndex
              ? cartesianGridLeftOffset +
                (activeTooltipIndex * cartesianGridWidth) / data.length
              : cartesianGridLeftOffset,
            y: cartesianGridHeight - 20,
          }}
          wrapperStyle={{ zIndex: 100 }}
          content={
            <CustomTooltip
              compareMode={compareMode}
              isMobileView={isMobileView}
              leftTooltipHeight={leftTooltipHeight}
              leftTooltipWidth={leftTooltipWidth}
              arrowWidth={arrowWidth}
              cartesianGridWidth={cartesianGridWidth}
              leftTooltipRef={leftTooltipRef}
              marketEnabled={marketEnabled}
              marketLastYearEnabled={marketLastYearEnabled}
              fromYear={fromYear}
              toYear={toYear}
            />
          }
          cursor={false}
          allowEscapeViewBox={{ x: true, y: true }}
        />

        {/* Show bars if chartType is bar */}
        
        {chartType === "bar" &&          
          occupancyKeys.map((key, index) => {

            const valueKey = `${toYear}_occupancy_rate_${key}`;    

            return (
              <Bar
                // key={`${toYear}_${key}`}
                barSize={barWidth}
                stackId={`stack_${toYear}`}
                dataKey={`${toYear}_occupancy_rate_${key}`}
                isAnimationActive={false}
                fill={colorsHex[key]}
                shape={(props) => {
                  const { x, y, width, height, index } = props as any;
                  const isTop = topStackKeysForToYear[index] === valueKey;
                  const r = barRadius;

                  if (isTop) {
                    // Top stack: round only top corners
                    const path = `
                      M${x},${y + height} 
                      L${x},${y + r} 
                      Q${x},${y} ${x + r},${y} 
                      L${x + width - r},${y} 
                      Q${x + width},${y} ${x + width},${y + r} 
                      L${x + width},${y + height} 
                      Z
                    `;
                    return <path d={path} fill={colorsHex[key]} />;
                  } else {
                    // Other stacks: normal rectangle
                    return <rect x={x} y={y} width={width} height={height} fill={colorsHex[key]} />;
                  }
                }}
              />
            );
        })}

        {/* Show bars for comparison if chartType is bar */}
        {compareMode &&
          fromYear !== toYear &&
          chartType === "bar" &&
          occupancyKeys.map((key, index) => {

            const valueKey = `${fromYear}_occupancy_rate_${key}`;

            return (
              <Bar
                // key={`${fromYear}_${key}`}
                barSize={barWidth}
                stackId={`stack_${fromYear}`}
                dataKey={`${fromYear}_occupancy_rate_${key}`}
                isAnimationActive={false}
                fill={compareColorsHex[key]}
                shape={(props) => {
                  const { x, y, width, height, index } = props as any;
                  const isTop = topStackKeysForFromYear[index] === valueKey;
                  const r = barRadius;

                  if (isTop) {
                    // Top stack: round only top corners
                    const path = `
                      M${x},${y + height} 
                      L${x},${y + r} 
                      Q${x},${y} ${x + r},${y} 
                      L${x + width - r},${y} 
                      Q${x + width},${y} ${x + width},${y + r} 
                      L${x + width},${y + height} 
                      Z
                    `;
                    return <path d={path} fill={compareColorsHex[key]} />;
                  } else {
                    // Other stacks: normal rectangle
                    return <rect x={x} y={y} width={width} height={height} fill={compareColorsHex[key]} />;
                  }
                }}
              />
            );
        })}

        {/* Show lines if chartType is line */}
        {chartType === "line" &&
          occupancyKeys.map((key) => (
            <Line
              // key={`${toYear}_${key}`}
              dataKey={`${toYear}_occupancy_rate_${key}`}
              stroke={colorsHex[key]}
              isAnimationActive={false}
              strokeWidth={3}
              type="monotone"
              filter="drop-shadow(0px 7px 5px rgba(93, 98, 147, 0.43))"
              dot={false}
            />
          ))}

        {/* Compare Year Lines if chartType is line */}
        {compareMode &&
          fromYear !== toYear &&
          chartType === "line" &&
          occupancyKeys.map((key) => (
            <Line
              // key={`${fromYear}_${key}`}
              dataKey={`${fromYear}_occupancy_rate_${key}`}
              stroke={compareColorsHex[key]}
              isAnimationActive={false}
              strokeWidth={3}
              type="monotone"
              filter="drop-shadow(0px 7px 5px rgba(93, 98, 147, 0.43))"
              dot={false}
            />
          ))}

        {/* Market lines */}
        {marketEnabled && (
          <Line
            dataKey="market"
            stroke="#FDB022"
            isAnimationActive={false}
            strokeWidth={3}
            dot={false}
            type="monotone"
            filter="drop-shadow(0px 7px 5px rgba(93, 98, 147, 0.43))"
          />
        )}

        {marketLastYearEnabled && (
          <Line
            dataKey="marketLastYear"
            stroke="#FD853A"
            isAnimationActive={false}
            strokeWidth={3}
            type="monotone"
            filter="drop-shadow(0px 7px 5px rgba(93, 98, 147, 0.43))"
            dot={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
